// databasecontext.js

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { fetchRecentMessages, searchDatabaseMessages } from '../handlers/handleDatabaseConnection.js';

const DatabaseContext = createContext({
  messages: [],
  searchTerm: '',
  setSearchTerm: () => {},
  loadRecentMessages: async () => [],
  searchMessages: async () => [],
});

export const DatabaseProvider = ({ children, websocketUrl }) => {
  const [messages, setMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const loadRecentMessages = useCallback(async (limit = 100) => {
    const recentMessages = await fetchRecentMessages(limit, websocketUrl);
    setMessages(recentMessages);
    return recentMessages;
  }, [websocketUrl]);

  const searchMessages = useCallback(async (query, limit = 50) => {
    const trimmedQuery = query.trim();
    setSearchTerm(trimmedQuery);
    if (!trimmedQuery) {
      return loadRecentMessages();
    }
    const searchResults = await searchDatabaseMessages(trimmedQuery, limit, websocketUrl);
    setMessages(searchResults);
    return searchResults;
  }, [loadRecentMessages, websocketUrl]);

  useEffect(() => {
    if (!websocketUrl) {
      return;
    }

    let cancelled = false;

    const loadWithRetry = async () => {
      let lastError;

      for (let attempt = 1; attempt <= 3; attempt += 1) {
        try {
          if (cancelled) {
            return;
          }
          await loadRecentMessages();
          return;
        } catch (error) {
          lastError = error;
          if (attempt < 3) {
            await delay(attempt * 500);
          }
        }
      }

      if (!cancelled && lastError) {
        console.error("Error fetching messages from database after retries:", lastError);
      }
    };

    loadWithRetry();

    return () => {
      cancelled = true;
    };
  }, [loadRecentMessages, websocketUrl]);

  const value = useMemo(() => ({
    messages,
    searchTerm,
    setSearchTerm,
    loadRecentMessages,
    searchMessages,
  }), [messages, searchTerm, loadRecentMessages, searchMessages]);

  return (
    <DatabaseContext.Provider value={ value }>
      {children}
    </DatabaseContext.Provider>
  );
};

export const useDatabase = () => {
  return useContext(DatabaseContext);
};
